import { useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '@platform/core/lib/routes';
import { useIsObserver, useOnboardWorkspaceMutation } from '@platform/features/auth';
import { onboardingGroups, useOpenInWorkspace } from '@platform/navigation';
import { useGetCatalogQuery, useMeQuery } from '@platform/state/api';
import { Spinner } from '@platform/ui';
import { authErrorKey } from '../authErrorKey';
import { authRoutes } from '../authRoutes';
import { OnboardModules } from './OnboardModules';
import { onboardGroups, onboardModules } from './onboardGroups';
import { onboardSlug } from './onboardSlug';
import './onboard.css';

/**
 * «Створити робочий простір» (прототип v4 screen 'onboard', поза оболонкою): логотип, заголовок і пояснення, назва з адресою, «Модулі» —
 * обов'язкові («завжди включені») і необов'язкові плитками за категоріями прототипу з лічильником, «Додати зразкові записи для
 * ознайомлення» (перший простір — після реєстрації чи входу без просторів — увімкнено, з меню просторів — вимкнено, як newWs прототипу;
 * стан маршруту { sample } має перевагу), «Скасувати» (якщо простори вже є) і
 * «Створити робочий простір» (POST /workspaces з адресою, пакетами й зразковими записами) — далі Головна нового простору.
 */
export function OnboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const open = useOpenInWorkspace();
  const observer = useIsObserver();
  const { data: me, isLoading, isError, refetch } = useMeQuery();
  const { data: catalog } = useGetCatalogQuery({});
  const [create, creating] = useOnboardWorkspaceMutation();
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const routeSample = (state as { sample?: boolean } | null)?.sample;
  const [sample, setSample] = useState<boolean | null>(typeof routeSample === 'boolean' ? routeSample : null);
  const [error, setError] = useState('');
  const groups = useMemo(() => onboardGroups(onboardingGroups(), catalog), [catalog]);
  if (isLoading) return <div className="login"><Spinner /></div>;
  if (isError || !me) return <Navigate to={authRoutes.login()} replace />;
  if (observer) return <Navigate to={routes.root()} replace />;
  const back = me.workspaces.length > 0;
  const sampleOn = sample ?? !me.workspaces.length;
  const submit = () => {
    if (!name.trim() || creating.isLoading) return;
    setError('');
    const modules = onboardModules(groups, picked);
    create({ name: name.trim(), slug: onboardSlug(name), modules, sample: sampleOn }).unwrap().then(async (w) => {
      await refetch();
      open(w.id, routes.home());
    }, (e: { code?: string }) => setError(t(authErrorKey(e.code))));
  };
  return (
    <div className="ob">
      <div className="ob__box">
        <div className="ob__brand"><span className="ob__logo" aria-hidden="true">P</span><span className="ob__product">{t('auth.product')}</span></div>
        <div className="ob__titles"><h1>{t('auth.onboard.title')}</h1><div className="ob__sub">{t('auth.onboard.sub')}</div></div>
        <label className="ob__field">
          <span className="ob__label">{t('auth.onboard.name')}</span>
          <input className="ob__input" value={name} placeholder={t('auth.onboard.placeholder')} aria-label={t('auth.onboard.placeholder')}
            onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
          <span className="ob__slug">parliamentarian-office.com/{onboardSlug(name)}</span>
        </label>
        <OnboardModules groups={groups} picked={picked} onToggle={(module) => setPicked((p) => ({ ...p, [module]: !p[module] }))} />
        <label className="ob__sample">
          <input type="checkbox" checked={sampleOn} onChange={() => setSample(!sampleOn)} />
          <span>{t('auth.onboard.sample')}</span>
        </label>
        {error && <div className="ob__error" role="alert">{error}</div>}
        <div className="ob__actions">
          {back && <button type="button" className="ob__btn" onClick={() => navigate(routes.home())}>{t('auth.onboard.cancel')}</button>}
          <button type="button" className="ob__btn ob__btn--primary" disabled={!name.trim() || creating.isLoading} onClick={submit}>{t('auth.onboard.create')}</button>
        </div>
      </div>
    </div>
  );
}
